import type { Court } from '../../types/tournament';
import {
  IMatchScheduleItem,
  IScheduleConstraints,
  IScheduleDayConstraints,
  IScheduleItem,
  ITournamentSchedule,
} from '../../types/schedule';
import {
  getMatchDurationFromConstraints,
  getMatchStartMs,
  getMatchesForTournamentDay,
  getTournamentDayDisplayBounds,
  resolveTournamentDayForMatch,
} from '../../pages/tourDetails/Schedule/components/schedule.helpers';

const FREE_SLOT_MINUTES = 5;

export type CourtKey = { dayKey: number; courtId: number };

export const courtKeyId = (key: CourtKey) => `${key.dayKey}:${key.courtId}`;

export function courtKeyForMatch(
  match: IMatchScheduleItem,
  constraints: IScheduleConstraints,
  timeZone: string,
  allMatches?: IMatchScheduleItem[]
): CourtKey | null {
  const day = resolveTournamentDayForMatch(match, constraints, timeZone, allMatches);
  if (!day || !match.court) return null;
  return { dayKey: day.dayKey, courtId: match.court.id };
}

export function courtKeyFromSlot(dayKey: number, courtId: number): CourtKey {
  return { dayKey, courtId };
}

export function rebuildCourtTimeline(
  allMatches: IMatchScheduleItem[],
  dayConstraints: IScheduleDayConstraints,
  court: Court,
  constraints: IScheduleConstraints,
  timeZone: string
): IScheduleItem[] {
  const dayMatches = getMatchesForTournamentDay(
    dayConstraints,
    allMatches,
    constraints,
    timeZone
  );

  const { effectiveStartMs, sessionEndMs: effectiveEndMs } = getTournamentDayDisplayBounds(
    dayConstraints,
    dayMatches,
    constraints,
    timeZone
  );

  const courtMatches = dayMatches
    .filter((m) => m.court.id === court.id)
    .sort((a, b) => (getMatchStartMs(a) ?? 0) - (getMatchStartMs(b) ?? 0));

  const timeline: IScheduleItem[] = [];
  let currentTime = effectiveStartMs;

  for (const match of courtMatches) {
    const updatedMatch = { ...match };
    const matchStart = getMatchStartMs(updatedMatch);
    if (matchStart == null) continue;

    while (currentTime < matchStart) {
      const diffMins = (matchStart - currentTime) / 60000;
      const dur = Math.min(FREE_SLOT_MINUTES, diffMins);
      timeline.push({
        isFreeSlot: true,
        day: dayConstraints.dayKey,
        court,
        scheduleDate: new Date(currentTime).toISOString(),
        duration: dur,
      });
      currentTime += dur * 60000;
    }

    const matchDuration =
      updatedMatch.duration ||
      getMatchDurationFromConstraints(updatedMatch, constraints) ||
      30;
    updatedMatch.duration = matchDuration;
    const matchEnd = matchStart + matchDuration * 60000;

    updatedMatch.scheduleEndDate = new Date(matchEnd).toISOString();
    timeline.push(updatedMatch);

    currentTime = matchEnd;
  }

  while (currentTime < effectiveEndMs) {
    const diffMins = (effectiveEndMs - currentTime) / 60000;
    const dur = Math.min(FREE_SLOT_MINUTES, diffMins);
    timeline.push({
      isFreeSlot: true,
      day: dayConstraints.dayKey,
      court,
      scheduleDate: new Date(currentTime).toISOString(),
      duration: dur,
    });
    currentTime += dur * 60000;
  }

  return timeline;
}

export function patchScheduleCourts(
  schedule: ITournamentSchedule,
  allMatches: IMatchScheduleItem[],
  constraints: IScheduleConstraints,
  timeZone: string,
  courtKeys: CourtKey[]
): ITournamentSchedule {
  if (courtKeys.length === 0) return schedule;

  const keysSet = new Set(courtKeys.map(courtKeyId));

  return {
    days: schedule.days.map((day) => {
      const dayConstraints = constraints.days.find((d) => d.dayKey === day.dayKey);
      if (!dayConstraints) return day;

      const dayHasPatch = day.courts.some((c) => keysSet.has(courtKeyId({ dayKey: day.dayKey, courtId: c.court.id })));
      if (!dayHasPatch) return day;

      return {
        ...day,
        courts: day.courts.map((courtSchedule) => {
          const key = courtKeyId({ dayKey: day.dayKey, courtId: courtSchedule.court.id });
          if (!keysSet.has(key)) return courtSchedule;

          const court =
            dayConstraints.courts.find((c) => c.id === courtSchedule.court.id) ??
            courtSchedule.court;

          return {
            court: courtSchedule.court,
            matches: rebuildCourtTimeline(
              allMatches,
              dayConstraints,
              court,
              constraints,
              timeZone
            ),
          };
        }),
      };
    }),
  };
}

export function uniqueCourtKeys(keys: CourtKey[]): CourtKey[] {
  const seen = new Set<string>();
  const out: CourtKey[] = [];
  for (const key of keys) {
    const id = courtKeyId(key);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(key);
  }
  return out;
}

export function patchScheduleFromFlatMatches(
  schedule: ITournamentSchedule,
  allMatches: IMatchScheduleItem[],
  constraints: IScheduleConstraints,
  timeZone: string,
  courtKeys: CourtKey[]
): ITournamentSchedule {
  return patchScheduleCourts(
    schedule,
    allMatches,
    constraints,
    timeZone,
    uniqueCourtKeys(courtKeys)
  );
}
